import { ArrowPathIcon, CheckCircleIcon, ExclamationTriangleIcon, MinusCircleIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";

import { IPart } from "../../../models/Part";
import {
  IMarketplaceListingSummary,
  Marketplace,
  MarketplaceListingStatus,
  listingStatusLabel,
  marketplaceLabel,
} from "../../../models/Publishing";
import formatDateTime from "../../../utils/formatDateTime";
import { resolveMarketplaceStatuses } from "../../shared/MarketplacePills";

const tone: Record<number, string> = {
  [MarketplaceListingStatus.Live]: "text-green-700",
  [MarketplaceListingStatus.Sold]: "text-green-700",
  [MarketplaceListingStatus.Failed]: "text-red-700",
  [MarketplaceListingStatus.Publishing]: "text-amber-700",
  [MarketplaceListingStatus.Withdrawn]: "text-gray-500",
  [MarketplaceListingStatus.NotListed]: "text-gray-400",
};

function StatusIcon({ status }: { status: MarketplaceListingStatus }) {
  const cls = `h-4 w-4 shrink-0 ${tone[status] ?? "text-gray-400"}`;

  switch (status) {
    case MarketplaceListingStatus.Live:
    case MarketplaceListingStatus.Sold:
      return <CheckCircleIcon className={cls} aria-hidden="true" />;
    case MarketplaceListingStatus.Failed:
      return <ExclamationTriangleIcon className={cls} aria-hidden="true" />;
    case MarketplaceListingStatus.Publishing:
      return <ArrowPathIcon className={`${cls} animate-spin`} aria-hidden="true" />;
    default:
      return <MinusCircleIcon className={cls} aria-hidden="true" />;
  }
}

const externalHref = (row: IMarketplaceListingSummary) => {
  if (row.listingUrl) return row.listingUrl;
  if (row.marketplace === Marketplace.Ebay && row.externalId) return `https://www.ebay.co.uk/itm/${row.externalId}`;
  return null;
};

/**
 * Per-marketplace outcome for a part, one line each: status, when, and the marketplace's own
 * error where it refused the listing. `compact` fits a table cell (error trimmed to one line);
 * the full form is for the detail modal and shows every response in full.
 */
export default function MarketplaceOutcomes({
  part,
  compact = false,
}: {
  // Null when the part behind the batch item has since been deleted; the row still shows.
  part: IPart | null | undefined;
  compact?: boolean;
}) {
  const rows = part ? resolveMarketplaceStatuses(part) : [];

  if (rows.length === 0) {
    return compact ? null : <p className="text-sm text-gray-500">No marketplace has answered for this part yet.</p>;
  }

  if (compact) {
    // The width cap is what makes the truncate below do anything. A table cell sizes itself to its
    // content, so an unbounded row let a marketplace's own error - eBay and PartPro both return
    // whole JSON bodies - set the column's width and push the table off the screen sideways. The
    // full text is still on the row's title attribute and in the detail modal.
    return (
      <ul className="w-full max-w-xs space-y-0.5 text-left">
        {rows.map((row) => {
          const label = marketplaceLabel[row.marketplace] ?? `Marketplace ${row.marketplace}`;
          const failed = row.status === MarketplaceListingStatus.Failed;

          return (
            <li key={row.marketplace} className="flex items-center gap-1 text-xs">
              <StatusIcon status={row.status} />
              <span className={`shrink-0 font-medium ${tone[row.status] ?? "text-gray-500"}`}>{label}</span>
              <span className="shrink-0 text-gray-500">{listingStatusLabel[row.status]}</span>
              {failed && row.lastError && (
                <span className="min-w-0 flex-1 truncate text-red-700" title={row.lastError}>
                  – {row.lastError}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="divide-y divide-gray-200 rounded border border-gray-200">
      {rows.map((row) => {
        const label = marketplaceLabel[row.marketplace] ?? `Marketplace ${row.marketplace}`;
        const href = externalHref(row);
        const when = row.lastAttemptedOn ?? row.publishedOn;

        return (
          <div key={row.marketplace} className="p-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <StatusIcon status={row.status} />
              <span className="font-semibold text-gray-900">{label}</span>
              <span className={`text-sm font-medium ${tone[row.status] ?? "text-gray-500"}`}>
                {listingStatusLabel[row.status]}
              </span>
              {row.executedBySync && (
                <span className="rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                  via Wilma Sync
                </span>
              )}
              {when && (
                <span className="ml-auto text-xs text-gray-500">
                  {formatDateTime(when, DateTime.DATETIME_SHORT)}
                </span>
              )}
            </div>

            {(row.externalId || href) && (
              <div className="mt-1 text-xs text-gray-600">
                {href ? (
                  <a href={href} target="_blank" rel="noreferrer" className="text-primary underline" onClick={(e) => e.stopPropagation()}>
                    {row.externalId ?? href}
                  </a>
                ) : (
                  <span>{row.externalId}</span>
                )}
              </div>
            )}

            {row.lastError && (
              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded border bg-red-50 p-2 text-xs text-red-800">
                {row.lastError}
              </pre>
            )}
          </div>
        );
      })}
    </div>
  );
}
